import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Loader2, MapPin, CalendarDays, Clock, Ticket, Check, CheckCircle2, AlertCircle, FileDown, Wine } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function EventPage() {
  const { slug } = useParams();
  const [ev, setEv] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [payState, setPayState] = useState(null); // null|checking|paid|failed|cancelled
  const [paidInfo, setPaidInfo] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyItem, setBuyItem] = useState(null);    // {item_type, tier} tier null for tickets
  const [qty, setQty] = useState(2);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [org, setOrg] = useState('');
  const [attendees, setAttendees] = useState('');
  const [coverFees, setCoverFees] = useState(false);
  const [payMethod, setPayMethod] = useState('card'); // card | check (sponsorships)
  const [checkInfo, setCheckInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events/${slug}`).then(r => setEv(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const poll = useCallback(async (sid, attempts = 0) => {
    if (attempts >= 10) { setPayState('failed'); return; }
    try {
      const { data } = await axios.get(`${API}/events/orders/status/${sid}`);
      if (data.payment_status === 'paid') { setPaidInfo(data); setPayState('paid'); return; }
      if (data.status === 'expired') { setPayState('failed'); return; }
      setTimeout(() => poll(sid, attempts + 1), 2000);
    } catch { setPayState('failed'); }
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('session_id')) { setPayState('checking'); poll(p.get('session_id')); }
    else if (p.get('cancelled')) setPayState('cancelled');
  }, [poll]);

  const openBuy = (item_type, tier = null) => {
    setBuyItem({ item_type, tier }); setError(''); setPayMethod('card'); setBuyOpen(true);
    // If embedded in an iframe, ask the host page to scroll so the modal is visible.
    // Requires the host page to run the listener snippet from the embed code.
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(
          { type: 'tns-modal-open', source: 'tns-event', slug },
          '*'
        );
      }
    } catch { /* ignore cross-origin blocks */ }
  };

  const attendeeList = attendees.split('\n').map(s => s.trim()).filter(Boolean);

  const checkout = async () => {
    setError('');
    if (!name.trim() || !email.includes('@')) { setError('Please enter your name and a valid email.'); return; }
    setBusy(true);
    try {
      if (buyItem.item_type === 'sponsorship' && payMethod === 'check') {
        const { data } = await axios.post(`${API}/events/${slug}/offline-order`, {
          tier_key: buyItem.tier?.key,
          purchaser_name: name.trim(),
          purchaser_email: email.trim(),
          org,
          attendees: attendeeList,
        });
        setCheckInfo(data);
        setBuyOpen(false);
        setPayState('check_ok');
        window.scrollTo({ top: 400, behavior: 'smooth' });
        setBusy(false);
        return;
      }
      const body = {
        item_type: buyItem.item_type,
        quantity: (buyItem.item_type === 'ticket' || buyItem.item_type === 'table') ? Number(qty) : 1,
        tier_key: buyItem.tier?.key,
        purchaser_name: name.trim(),
        purchaser_email: email.trim(),
        org,
        cover_fees: coverFees,
        attendees: attendeeList,
        // If we're inside an iframe embed, Stripe must open in the top browser window
        // (Stripe blocks being loaded inside iframes) — send the top origin so the
        // post-payment redirect lands on the host page.
        origin_url: (() => {
          try { return window.top.location.origin; }
          catch { return window.location.origin; }
        })(),
      };
      const { data } = await axios.post(`${API}/events/${slug}/checkout`, body);
      // Break out of the iframe. Stripe refuses to render inside an iframe
      // (X-Frame-Options: DENY), which is why buyers on tnswork.org were seeing
      // a blank screen instead of the payment page.
      try {
        if (window.top && window.top !== window) {
          window.top.location.href = data.url;
        } else {
          window.location.href = data.url;
        }
      } catch {
        // Cross-origin block on window.top — fall back to explicitly targeting _top.
        window.open(data.url, '_top');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start checkout.');
      setBusy(false);
    }
  };

  if (notFound) return <div className="min-h-screen flex items-center justify-center bg-[#F7F4EF] text-gray-600">Event not found.</div>;
  if (!ev) return <div className="min-h-screen flex items-center justify-center bg-[#F7F4EF]"><Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" /></div>;

  const unitPrice = ev.current_ticket_price ?? ev.ticket_price;
  const closed = !!ev.sales_closed;
  const baseTotal = buyItem?.item_type === 'ticket' ? unitPrice * Number(qty || 0)
    : buyItem?.item_type === 'table' ? (ev.table_price || 0) * Number(qty || 0)
    : buyItem?.tier?.price || 0;
  const feeApplies = !(buyItem?.item_type === 'sponsorship' && payMethod === 'check');
  const feeAmt = coverFees && feeApplies ? Math.round(baseTotal * 3) / 100 : 0;
  const total = baseTotal + feeAmt;

  return (
    <div className="min-h-screen bg-[#F7F4EF]" data-testid="event-page">
      {/* Hero */}
      <div className="relative h-[420px] overflow-hidden">
        {ev.hero_fit === 'contain' ? (
          <>
            <img src={ev.hero_image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover blur-xl scale-110 opacity-60" />
            <img src={ev.hero_image} alt={ev.name} className="absolute inset-0 w-full h-full object-contain" />
          </>
        ) : (
          <img src={ev.hero_image} alt={ev.name} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#2B1509]/85 via-[#2B1509]/40 to-transparent" />
        <div className="relative h-full max-w-5xl mx-auto px-6 flex flex-col justify-end pb-10 text-white">
          <p className="uppercase tracking-[0.3em] text-amber-200 text-xs mb-2">The Next Step Programs presents</p>
          <h1 className="text-5xl sm:text-6xl font-medium" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{ev.name}</h1>
          <p className="text-white/80 mt-2 max-w-2xl">{ev.tagline}</p>
          {closed && (
            <span className="inline-block mt-3 px-3 py-1 rounded-full bg-amber-200 text-[#2B1509] text-xs font-semibold uppercase tracking-wider w-fit" data-testid="sales-closed-badge">
              Ticket sales closed
            </span>
          )}
          <div className="flex flex-wrap gap-4 mt-4 text-sm">
            <span className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-amber-200" /> {ev.event_date}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-amber-200" /> {ev.event_time}</span>
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-amber-200" /> {ev.venue}, {ev.venue_address}</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-10">
        {/* Payment states */}
        {payState === 'checking' && (
          <Card data-testid="event-checking"><CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0] mx-auto mb-3" />
            <p className="font-medium">Confirming your purchase…</p>
          </CardContent></Card>
        )}
        {payState === 'paid' && (
          <Card className="border-emerald-300" data-testid="event-success"><CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-2xl" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{ev.success_heading || 'Grazie mille — you\u2019re in!'}</h2>
            <p className="text-gray-600 mt-2">
              {paidInfo?.item_label} confirmed{paidInfo?.seats ? ` · ${paidInfo.seats} seat${paidInfo.seats !== 1 ? 's' : ''} reserved` : ''}.
              A Stripe receipt is on its way to your email. Your ticket confirmation from TNS will arrive via email within 72 hours. We can&apos;t wait to see you there!
            </p>
          </CardContent></Card>
        )}
        {payState === 'check_ok' && (
          <Card className="border-emerald-300" data-testid="event-check-success"><CardContent className="p-8 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
            <h2 className="text-2xl" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Sponsorship reserved!</h2>
            <p className="text-gray-600 mt-2">{checkInfo?.tier} — {fmt$(checkInfo?.amount)} · paying by check (no card fees).</p>
            <p className="text-sm text-gray-600 mt-3 max-w-lg mx-auto">{checkInfo?.instructions}</p>
          </CardContent></Card>
        )}
        {payState === 'cancelled' && (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm" data-testid="event-cancelled">
            Checkout was cancelled — no charge was made. Your seats are still available below.
          </div>
        )}
        {payState === 'failed' && (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm flex items-center gap-2" data-testid="event-failed">
            <AlertCircle className="h-4 w-4" /> We couldn&apos;t confirm the payment yet. If you completed checkout, watch your email for a Stripe receipt.
          </div>
        )}

        {/* About + tickets */}
        <div className="grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3">
            <h2 className="text-2xl mb-3" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{ev.about_heading || 'An evening to remember'}</h2>
            <p className="text-gray-700 leading-relaxed">{ev.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {(ev.highlights || ['Emcee & comedian', 'Music & entertainment', 'Open bar', 'Catered cuisine', 'Vineyard views']).map(f => (
                <span key={f} className="px-3 py-1 rounded-full bg-white border border-[#EAE5F0] text-sm text-[#6B5E7D] flex items-center gap-1">
                  <Wine className="h-3.5 w-3.5 text-[#B71C5C]" /> {f}
                </span>
              ))}
            </div>
          </div>
          <Card className="md:col-span-2 border-[#6B3FA0]/20">
            <CardContent className="p-6 text-center">
              <Ticket className="h-8 w-8 text-[#6B3FA0] mx-auto mb-2" />
              {closed ? (
                <div data-testid="tickets-closed-card">
                  <h3 className="text-xl font-medium mb-2" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Ticket sales have closed</h3>
                  <div className="text-left text-sm text-gray-600 space-y-2 mb-4 bg-[#F7F4EF] rounded-xl p-4">
                    <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#6B3FA0]" /> {ev.event_date}</p>
                    <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#6B3FA0]" /> {ev.event_time}</p>
                    <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#6B3FA0]" /> {ev.venue}</p>
                  </div>
                  <p className="text-sm text-gray-500">
                    Thank you for the incredible response! Questions about an existing order? Email{' '}
                    <a className="text-[#6B3FA0] hover:underline" href={`mailto:${ev.contact_email}`}>{ev.contact_email}</a>
                  </p>
                </div>
              ) : (
                <>
                  {ev.early_bird_active ? (
                    <>
                      <span className="inline-block px-3 py-0.5 rounded-full bg-[#B71C5C] text-white text-[11px] font-semibold uppercase tracking-wide mb-1" data-testid="early-bird-badge">
                        Early bird
                      </span>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-2xl text-gray-400 line-through" data-testid="regular-price-strike">{fmt$(ev.ticket_price)}</span>
                        <span className="text-4xl font-medium" style={{ fontFamily: 'Fraunces, Georgia, serif' }} data-testid="ticket-price">{fmt$(ev.current_ticket_price)}</span>
                      </div>
                      <div className="text-sm text-gray-500 mb-1">per ticket</div>
                      <div className="text-xs text-[#B71C5C] font-medium mb-4" data-testid="early-bird-deadline">
                        Early bird pricing ends August 18
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl font-medium" style={{ fontFamily: 'Fraunces, Georgia, serif' }} data-testid="ticket-price">{fmt$(ev.current_ticket_price ?? ev.ticket_price)}</div>
                      <div className="text-sm text-gray-500 mb-4">per ticket</div>
                    </>
                  )}
                  <Button className="w-full h-11 text-base" onClick={() => openBuy('ticket')} data-testid="buy-tickets-btn">
                    Buy tickets
                  </Button>
                  {ev.table_price && (
                    <Button variant="outline" className="w-full h-11 text-base mt-2 border-[#6B3FA0]/40 text-[#6B3FA0]"
                            onClick={() => openBuy('table')} data-testid="buy-table-btn">
                      Reserve a table of {ev.table_seats || 8} — {fmt$(ev.table_price)}
                    </Button>
                  )}
                  <p className="text-[11px] text-gray-400 mt-3">Secure checkout by Stripe</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Event program flipbook */}
        {ev.program_url && (
          <div data-testid="program-section">
            <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
              <h2 className="text-3xl" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Event Program</h2>
              <a href={ev.program_url} target="_blank" rel="noreferrer"
                 className="text-sm text-[#6B3FA0] font-medium hover:underline" data-testid="program-fullscreen-link">
                Open full screen →
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#6B3FA0]/15 shadow-sm bg-white">
              <iframe src={ev.program_url} title={`${ev.name} program booklet`} allowFullScreen
                      className="w-full block" style={{ height: '72vh', minHeight: 420, border: 'none' }}
                      data-testid="program-iframe" />
            </div>
          </div>
        )}

        {/* Sponsorships — hidden once sales close */}
        {!closed && (
        <div>
          <div className="flex items-end justify-between flex-wrap gap-2 mb-4">
            <div>
              <h2 className="text-2xl" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Sponsorship opportunities</h2>
              <p className="text-sm text-gray-500">{ev.sponsor_subtitle || 'Put your name behind inclusive housing — and enjoy the best seats in the vineyard.'}</p>
            </div>
            {ev.deck_url && (
              <a href={ev.deck_url} target="_blank" rel="noreferrer"
                 className="text-sm text-[#6B3FA0] hover:underline flex items-center gap-1" data-testid="deck-download">
                <FileDown className="h-4 w-4" /> Download sponsorship deck (PDF)
              </a>
            )}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ev.tiers.map((t, i) => (
              <Card key={t.key} className={i === 0 ? 'border-[#B71C5C]/40 relative' : ''} data-testid={`tier-${t.key}`}>
                {i === 0 && <span className="absolute -top-2.5 left-4 px-2 py-0.5 rounded-full bg-[#B71C5C] text-white text-[10px] font-semibold uppercase tracking-wide">Presenting</span>}
                <CardContent className="p-5 flex flex-col h-full">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#B71C5C] font-semibold">{t.name}</div>
                  <div className="text-lg font-semibold text-[#2B1D3D]">{t.title}</div>
                  <div className="text-3xl my-2 font-medium" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{fmt$(t.price)}</div>
                  <ul className="space-y-1.5 text-sm text-gray-600 flex-1 mb-4">
                    {t.benefits.map(b => (
                      <li key={b} className="flex gap-2"><Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {b}</li>
                    ))}
                  </ul>
                  <Button variant={i === 0 ? 'default' : 'outline'} className="w-full"
                          onClick={() => openBuy('sponsorship', t)} data-testid={`sponsor-${t.key}-btn`}>
                    Become a sponsor
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 mt-4">{ev.tax_note}</p>
        </div>
        )}

        {/* Can't attend — donate instead */}
        <Card className="bg-[#2E1D49] border-0 text-white" data-testid="cant-attend-card">
          <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl text-white" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                {closed ? 'You can still support the mission' : "Can't make it to the event?"}
              </h3>
              <p className="text-white/70 text-sm mt-1">
                {closed
                  ? (ev.donate_text_closed || 'Ticket and sponsorship sales have closed, but every gift still fuels inclusive housing for individuals with intellectual and developmental disabilities.')
                  : (ev.donate_text || 'You can still fuel inclusive housing with a gift of any size — every dollar counts toward the same mission.')}
              </p>
            </div>
            <Button className="bg-white text-[#5A2F8E] hover:bg-white/90 shrink-0"
                    onClick={() => {
                      const donateUrl = `/donate?campaign=${encodeURIComponent(ev.name)}`;
                      try {
                        if (window.top && window.top !== window) {
                          window.top.location.href = new URL(donateUrl, window.top.location.origin).href;
                        } else {
                          window.location.href = donateUrl;
                        }
                      } catch {
                        window.open(donateUrl, '_top');
                      }
                    }}
                    data-testid="donate-instead-btn">
              {closed ? 'Make a donation' : 'Make a donation instead'}
            </Button>
          </CardContent>
        </Card>

        {(ev.confirmed_sponsors || []).length > 0 && (
          <section className="border-t border-[#EAE5F0] pt-8" data-testid="confirmed-sponsors-section">
            <div className="text-center mb-8">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#5A2F8E]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                Thank You To Our Sponsors!
              </h2>
            </div>
            {Object.entries(
              (ev.confirmed_sponsors || []).reduce((acc, s) => {
                (acc[s.tier_label] = acc[s.tier_label] || []).push(s);
                return acc;
              }, {})
            ).map(([tier, list]) => (
              <div key={tier} className="mb-8" data-testid={`sponsors-tier-${tier.replace(/\s+/g, '-').toLowerCase()}`}>
                <h3 className="text-lg font-semibold text-[#5A2F8E] border-b border-[#EAE5F0] pb-2 mb-4">
                  {tier}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {list.map(s => (
                    <SponsorCard key={s.id} sponsor={s} />
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}

        <div className="text-center text-sm text-gray-500 border-t border-[#EAE5F0] pt-6 space-y-3">
          <p>
            Questions? Email <a className="text-[#6B3FA0] hover:underline" href={`mailto:${ev.contact_email}`}>{ev.contact_email}</a> or call {ev.contact_phone}
          </p>
          {ev.charity_disclosure && (
            <p className="text-[10px] text-gray-400 max-w-3xl mx-auto leading-relaxed" data-testid="charity-disclosure">
              {ev.charity_disclosure}
            </p>
          )}
        </div>
      </div>

      {/* Purchase dialog */}
      <Dialog open={buyOpen} onOpenChange={o => !o && setBuyOpen(false)}>
        <DialogContent className="max-w-md top-8 translate-y-0 sm:top-16" data-testid="event-buy-dialog">
          <DialogHeader>
            <DialogTitle>
              {buyItem?.item_type === 'ticket' ? 'Buy tickets'
                : buyItem?.item_type === 'table' ? `Reserve a table of ${ev.table_seats || 8}`
                : `${buyItem?.tier?.name} — ${buyItem?.tier?.title}`}
            </DialogTitle>
            <DialogDescription>Secure payment powered by Stripe — a receipt will be emailed to you.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {(buyItem?.item_type === 'ticket' || buyItem?.item_type === 'table') && (
              <div>
                <Label>{buyItem?.item_type === 'table' ? 'Number of tables' : 'Number of tickets'}</Label>
                <div className="flex items-center gap-3 mt-1">
                  <Button size="icon" variant="outline" onClick={() => setQty(q => Math.max(1, q - 1))} data-testid="qty-minus">−</Button>
                  <span className="text-xl font-semibold w-8 text-center" data-testid="qty-value">{qty}</span>
                  <Button size="icon" variant="outline" onClick={() => setQty(q => Math.min(buyItem?.item_type === 'table' ? 5 : 20, q + 1))} data-testid="qty-plus">+</Button>
                  <span className="text-sm text-gray-500">× {fmt$(buyItem?.item_type === 'table' ? ev.table_price : unitPrice)}{buyItem?.item_type === 'ticket' && ev.early_bird_active ? ' early bird' : ''}</span>
                </div>
              </div>
            )}
            {buyItem?.item_type === 'sponsorship' && (
              <div>
                <Label>Payment method</Label>
                <div className="grid grid-cols-2 gap-2 mt-1" data-testid="pay-method-toggle">
                  <button onClick={() => setPayMethod('card')}
                          className={`py-2 rounded-full text-sm font-semibold border-2 transition-colors ${payMethod === 'card' ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#2B1D3D] border-[#EAE5F0]'}`}
                          data-testid="pay-card-btn">Card</button>
                  <button onClick={() => setPayMethod('check')}
                          className={`py-2 rounded-full text-sm font-semibold border-2 transition-colors ${payMethod === 'check' ? 'bg-[#6B3FA0] text-white border-[#6B3FA0]' : 'bg-white text-[#2B1D3D] border-[#EAE5F0]'}`}
                          data-testid="pay-check-btn">Check (no fees)</button>
                </div>
                {payMethod === 'check' && (
                  <p className="text-xs text-gray-500 mt-1">We&apos;ll reserve your sponsorship now and follow up with invoice &amp; mailing details — no card fees.</p>
                )}
              </div>
            )}
            <div>
              <Label htmlFor="e-name">Your name</Label>
              <Input id="e-name" value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" data-testid="event-name-input" />
            </div>
            <div>
              <Label htmlFor="e-email">Email</Label>
              <Input id="e-email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@example.com" data-testid="event-email-input" />
            </div>
            {buyItem?.item_type === 'sponsorship' && (
              <div>
                <Label htmlFor="e-org">Organization (for recognition)</Label>
                <Input id="e-org" value={org} onChange={e => setOrg(e.target.value)} placeholder="Company or family name" data-testid="event-org-input" />
              </div>
            )}
            {error && <p className="text-sm text-red-600" data-testid="event-buy-error">{error}</p>}
            <div>
              <Label htmlFor="e-attendees">Attendee names <span className="text-gray-400 font-normal">(optional, one per line)</span></Label>
              <textarea id="e-attendees" rows={2} value={attendees} onChange={e => setAttendees(e.target.value)}
                        placeholder={"Jane Smith\nJohn Smith"}
                        className="w-full mt-1 rounded-xl border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="event-attendees-input" />
            </div>
            {feeApplies && (
              <label className="flex items-start gap-2 text-sm cursor-pointer" data-testid="fee-cover-label">
                <input type="checkbox" checked={coverFees} onChange={e => setCoverFees(e.target.checked)}
                       className="h-4 w-4 mt-0.5" data-testid="fee-cover-checkbox" />
                <span>Add 3% ({fmt$(Math.round(baseTotal * 3) / 100)}) to cover card processing fees so 100% of your {buyItem?.item_type === 'ticket' ? 'ticket' : 'sponsorship'} supports the mission</span>
              </label>
            )}
            <Button className="w-full h-11" onClick={checkout} disabled={busy} data-testid="event-checkout-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> :
                (buyItem?.item_type === 'sponsorship' && payMethod === 'check')
                  ? `Reserve — pay ${fmt$(total)} by check`
                  : `Continue to payment — ${fmt$(total)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SponsorCard({ sponsor }) {
  const inner = sponsor.logo_url ? (
    <div className="h-24 flex items-center justify-center p-3">
      <img src={sponsor.logo_url} alt={sponsor.name}
           className="max-h-full max-w-full object-contain"
           onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.textContent = sponsor.name; }} />
    </div>
  ) : (
    <div className="h-24 flex items-center justify-center p-3 text-center">
      <span className="text-[#5A2F8E] font-semibold text-sm leading-tight">{sponsor.name}</span>
    </div>
  );
  const cardCls = "border border-[#EAE5F0] rounded-md bg-white hover:shadow-md transition-shadow";
  if (sponsor.website_url) {
    return (
      <a href={sponsor.website_url} target="_blank" rel="noopener noreferrer"
         className={`${cardCls} block`} data-testid={`sponsor-card-${sponsor.id}`}>{inner}</a>
    );
  }
  return <div className={cardCls} data-testid={`sponsor-card-${sponsor.id}`}>{inner}</div>;
}
